import Constants from '../Constants.js';
import ResourceEnumReducer from './ResourceEnumReducer.js';
const {
  getStatus,
  getResourceEnums,
  getError
} = ResourceEnumReducer;

/**
 * An interface for resource enums
 */
class ResourceEnums {
  /**
   * @param {string} resourceId the resource this is being created against
   * @param {object} resourceApi initialized api starting at the resource level
   * @param {boolean} isResourceLevel true if this is an instance at the resource level,
   *                                  false if this a project level ResourceEnum. Different
   *                                  methods (e.g., create, reset) are available in each case.
   */
  constructor({
    resourceId,
    resourceApi,
    isResourceLevel
  }) {
    this.resourceApi = resourceApi;
    this.resourceId = resourceId;
    this.isResourceLevel = isResourceLevel;
  }

  /**
   * @method createAction A creator for actions
   * @memberof ResourceEnums
   * @param {string} method The name of the method being created (e.g. 'list')
   * @param {string} status The status of the action (e.g. 'loading')
   * @param {function} func The async function that executes the action
   */
  createAction(method, status, func, enumType = null) {
    const resourceId = this.resourceId;
    return (dispatch, getState, opts) => {
      const api = this.resourceApi(dispatch, getState, opts, resourceId);
      dispatch({
        type: Constants.RESOURCE_ENUM_STATUS,
        enumType,
        status,
        resourceId
      });
      return func(dispatch, getState, {
        ...opts,
        api
      }, this.resourceId).catch(error => {
        dispatch({
          type: Constants.RESOURCE_ENUM_STATUS,
          enumType,
          resourceId,
          status: `${method}Error`,
          error
        });
        return Promise.reject(error);
      });
    };
  }

  /**
    * @method create An action to create an Enum type
    * @memberof ResourceEnums
    * @param {string} type The unique type of enum to create (e.g., 'VehicleColor' )
    * @param {object} [data] The data to include with the enum
    * @param {string} [data.name] Human readable name for enum
    * @param {string} [data.description] Description of enum
    * @param {object} [data.metadata] metadata for enum
    * @param {object[]} [data.values] Any values for the enum that should be created when enum type is created
    * @returns {promise}
    */
  create(type, data) {
    if (this.isResourceLevel) {
      throw Error('ResourceEnums cannot be created at a resource level. The headers can be only updated.');
    }
    const action = async (dispatch, getState, {
      api
    }, resourceId) => {
      const createEnumRes = await api.resourceEnums().create(type, data);
      dispatch({
        type: Constants.RESOURCE_ENUM_ADD,
        resourceId,
        data: createEnumRes
      });
      dispatch({
        type: Constants.RESOURCE_ENUM_STATUS,
        enumType: type,
        status: null,
        resourceId
      });
      return createEnumRes;
    };
    return this.createAction('create', 'creating', action, type);
  }

  /**
   * @method list An action to list all resource enums on the resource
   * @memberof ResourceEnums
   * @param {object} opts Options for the `resourceEnums.list( ...opts )` api call
   * @returns {promise}
   */
  list(opts = {}) {
    return this.createAction('list', 'loading', async (dispatch, getState, {
      api
    }, resourceId) => {
      const res = await api.resourceEnums().list(opts);
      dispatch({
        type: Constants.RESOURCE_ENUM_LIST,
        resourceId,
        list: res.items
      });
      dispatch({
        type: Constants.RESOURCE_ENUM_STATUS,
        status: null,
        resourceId
      });
      return res;
    });
  }

  /**
   * @method deleteAll An action to delete all enum types from project
   * @returns {promise}
   */
  deleteAll() {
    if (this.isResourceLevel) {
      throw Error('Resource enums must be deleted at the project level.');
    }
    return this.createAction('delete', 'deleting', async (dispatch, getState, {
      api
    }, resourceId) => {
      const res = await api.resourceEnums().deleteAll();
      dispatch({
        type: Constants.RESOURCE_ENUM_DELETE_ALL,
        resourceId
      });
      dispatch({
        type: Constants.RESOURCE_ENUM_STATUS,
        status: null,
        resourceId
      });
      return res;
    });
  }

  /**
   * @method getStatus Get the status of the Enum actions from Redux state
   * @memberof ResourceEnums
   * @param {string} [type] enum type to check
   * @returns {promise}
   */
  getStatus(state, type = null) {
    return getStatus(state, this.resourceId, type);
  }

  /**
     * @method getError Get the current error for the Enum actions from Redux state
     * @memberof ResourceEnums
     * @param {string} [type] enum type to check
     * @returns {promise}
     */
  getError(state, type = null) {
    return getError(state, this.resourceId, type);
  }

  /**
     * @method getEnums Get the project's list of Enums from Redux state
     * @memberof ResourceEnums
     * @returns {promise}
     */
  getResourceEnums(state) {
    return getResourceEnums(state, this.resourceId);
  }
}
export default ResourceEnums;